// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import i18n from "i18next";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { getWebUIResourceUrl } from "../../helpers";
import { logApiError } from "../../infra/apiError";
import { showDedupedErrorToast } from "../../infra/dedupedToast";
import { fetchJson } from "../../infra/fetchJson";
import { minifyGraphQLQuery } from "../minifyGraphQLQuery";
import { runtimeUIConfigAtom } from "../useRuntimeUIConfig";

export type ImageArchitecture = "x86_64" | "aarch64";

export interface Image {
  name: string;
  humanized_name: string;
  tag: string;
  registry: string;
  architecture: ImageArchitecture;
  digest: string;
  installed: boolean;
  resource_limits: {
    key: string;
    min: string;
    max: string;
  }[];
  labels: {
    key: string;
    value: string;
  }[];
}

/** API response type — the GraphQL API may return null entries in arrays. */
type ImageResponse = Omit<Image, "labels" | "resource_limits"> & {
  labels: ({ key: string; value: string } | null)[];
  resource_limits: ({ key: string; min: string; max: string } | null)[];
};

export class ImageRef {
  constructor(
    private registry: string,
    private name: string,
    private tag: string,
    private architecture: ImageArchitecture
  ) {}

  static fromImage(image: Image): ImageRef {
    return new ImageRef(
      image.registry,
      image.name,
      image.tag,
      image.architecture
    );
  }

  get identifier(): string {
    return `${this.registry}/${this.name}:${this.tag}@${this.architecture}`;
  }
}

type ImageMetadata = {
  name: string;
  description: string;
  group: string;
  tags: string[];
  icon: string;
  label: {
    category?: string;
    tag: string;
    color: string;
  }[];
};

export const imageListAtom = atom(async (get) => {
  const runtimeUIConfig = await get(runtimeUIConfigAtom);
  const response = await fetchJson<{
    data?: { images: ImageResponse[] };
    errors?: unknown[];
  }>("/func/admin/gql", {
    method: "POST",
    body: JSON.stringify({
      query: minifyGraphQLQuery(`
        query($installed: Boolean) {
          images(is_installed: $installed) {
            name
            humanized_name
            tag
            registry
            architecture
            digest
            installed
            resource_limits {
              key
              min
              max
            }
            labels {
              key
              value
            }
          }
        }
      `),
      variables: {
        installed: runtimeUIConfig.non_installed_images ? undefined : true,
      },
    }),
  });
  if (!response.data) {
    logApiError("imageListAtom", response.errors);
    return [];
  }
  const images: Image[] = ((response.data.images ?? []) as ImageResponse[]).map(
    (image) => ({
      ...image,
      labels: (image.labels ?? []).filter(
        (l): l is NonNullable<typeof l> => l != null
      ),
      resource_limits: (image.resource_limits ?? []).filter(
        (r): r is NonNullable<typeof r> => r != null
      ),
    })
  );
  return images.filter(
    (image) =>
      !image.labels.some(
        (label) =>
          label.key === "ai.backend.features" &&
          label.value.split(" ").includes("private")
      )
  );
});

export const imageMetadataAtom = atom(async (get) => {
  try {
    const imageMetadataUrl = getWebUIResourceUrl(
      "/resources/image_metadata.json"
    );
    if (!imageMetadataUrl) {
      // Route the misconfiguration through the same reporting path as a failed
      // fetch, with a message that names the cause instead of the bare
      // `TypeError: Invalid URL` that constructing the URL used to raise.
      throw new Error("No WebUI endpoint is configured");
    }
    const response = await fetch(imageMetadataUrl);
    return (await response.json()) as {
      imageInfo: {
        [key: string]: ImageMetadata;
      };
      tagAlias: {
        [key: string]: string;
      };
      tagReplace: {
        [key: string]: string;
      };
    };
  } catch (e) {
    logApiError("imageMetadataAtom:/resources/image_metadata.json", e);
    showDedupedErrorToast(
      "imageMetadata:load",
      i18n.t("error.FailedToLoadImageMetadata")
    );
    return { imageInfo: {}, tagAlias: {}, tagReplace: {} };
  }
});

const STORAGE_KEY = {
  IMAGE: {
    USE_RAW_NAME: "Backend_AI_FastTrack.image.rawNameEnabled",
  },
};

export const isRawImageInputEnabledAtom = atomWithStorage<boolean>(
  STORAGE_KEY.IMAGE.USE_RAW_NAME,
  false,
  {
    getItem(key: string, initialValue: boolean) {
      const item = localStorage.getItem(key);
      if (item === null) {
        return initialValue;
      }
      return item.toLowerCase() === "true";
    },
    setItem(key: string, newValue: boolean | null) {
      localStorage.setItem(key, newValue?.toString().toLowerCase() ?? "false");
    },
    removeItem: localStorage.removeItem,
  },
  { getOnInit: true }
);
